import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { computeText } from '@/locales/utils';
import { StepData } from '@/components/atoms/Wizard/Wizard';
import { stringifyError } from '@/utils/errors';
import { getUserToken } from '@/redux/user';
import {
    useAddPickupPointMutation,
    useGetLocationAutocompleteQuery,
    useGetLocationPlaceDetailsQuery,
    usePutPickupPointMutation,
} from '@/services/innovorder/pickupPoint/pickupPoint.endpoints';
import {
    CreatePickupPointFormValues,
    CreatePickupPointModalVMProps,
    OnStepChange,
} from './CreatePickupPointModal.type';
import { EditPickupPointStep } from '../EditPickupPointStep/EditPickupPointStep';

export const useCreatePickupPointModalVM = ({
    onHide,
    restaurantId,
    pickupPointId,
    name,
    address,
    additionalAddressInfo,
}: CreatePickupPointModalVMProps) => {
    const [currentStep, setCurrentStep] = useState(0);
    const intl = useIntl();
    const token = useSelector(getUserToken);
    const [putPickupPointById, { isLoading: isUpdateLoading, isSuccess: isUpdateSuccess, error: errorPut }] =
        usePutPickupPointMutation();
    const [postPickupPoint, { isLoading: isCreateLoading, isSuccess: isCreateSuccess, error: errorPost }] =
        useAddPickupPointMutation();
    const isSuccess = isCreateSuccess || isUpdateSuccess;
    const isLoading = isCreateLoading || isUpdateLoading;
    const error = errorPut || errorPost;

    // Add a session token state
    const [sessionToken] = useState(() => crypto.randomUUID());

    const errorMessage = React.useMemo(() => {
        if (error && 'status' in error) {
            const errorData = error.data as { extraData?: { errors?: { lineNumber: string; error: string }[] } };
            const extraData = errorData?.extraData
                ? errorData.extraData?.errors
                      ?.map(({ error: lineError, lineNumber }) => `${lineNumber}: ${lineError}`)
                      .join('\n')
                : '';
            return `${stringifyError(error)}${extraData ? `\n${extraData}` : ''}`;
        }
        return '';
    }, [error]);

    const form: UseFormReturn<CreatePickupPointFormValues> = useForm<CreatePickupPointFormValues>({
        mode: 'all',
        defaultValues: {
            name,
            address,
            additionalAddressInfo,
        },
    });

    const watchedAddress = form.watch('address');

    const { data: locationData } = useGetLocationAutocompleteQuery(
        {
            searchText: watchedAddress,
            sessiontoken: sessionToken,
            token: token || '',
        },
        { skip: watchedAddress.length <= 2 },
    );

    const { data: placeData } = useGetLocationPlaceDetailsQuery(
        {
            placeId: locationData?.suggestions?.[0]?.providerId || '',
            sessiontoken: sessionToken,
            token: token || '',
        },
        { skip: !locationData?.suggestions?.[0]?.providerId },
    );

    const handleStepChange =
        (type: 'next' | 'previous'): OnStepChange =>
        () => {
            setCurrentStep(type === 'previous' ? currentStep - 1 : currentStep + 1);
            return true;
        };

    const handleHide = useCallback(() => {
        if (!isLoading) {
            onHide();
        }
        return true;
    }, [isLoading, onHide]);

    const steps: StepData[] = [
        {
            title: `pickupPoint.modal.${pickupPointId ? 'editPickupPoint' : 'createPickupPoint'}`,
            component: (
                <EditPickupPointStep
                    onNext={handleStepChange('next')}
                    pickupPointId={pickupPointId || undefined}
                    isLoading={isLoading}
                />
            ),
            showHeader: true,
            showNext: false,
            showValidate: true,
            onCancel: handleHide,
            onNext: handleStepChange('next'),
            onPrevious: handleStepChange('previous'),
        },
    ];

    const onFormValidate = async () => {
        const { getValues, setError } = form;
        const { name, additionalAddressInfo } = getValues();

        if (!name) {
            setError('name', { message: computeText(intl, 'menu.modal.error.wrongName') });
            return;
        }
        if (!restaurantId) {
            return;
        }

        if (!pickupPointId) {
            await postPickupPoint({
                name,
                address: {
                    locality: placeData?.place.locality || '',
                    route: placeData?.place.route || '',
                    postalCode: placeData?.place.postalCode || '',
                    streetNumber: placeData?.place.streetNumber || '',
                    administrativeAreaLevel1: placeData?.place.administrativeAreaLevel1 || '',
                    administrativeAreaLevel2: placeData?.place.administrativeAreaLevel2 || '',
                    country: placeData?.place.country || '',
                    name: placeData?.place.name || '',
                    provider: placeData?.place.provider || '',
                    providerId: placeData?.place.providerId || '',
                    lat: placeData?.place.lat ?? 0,
                    lng: placeData?.place.lng ?? 0,
                    additionalInfo: additionalAddressInfo || '',
                },
                token: token || '',
                restaurantId: restaurantId.toString(),
            });
            return;
        } else {
            await putPickupPointById({
                pickupPointId,
                name,
                address: {
                    locality: placeData?.place.locality || '',
                    route: placeData?.place.route || '',
                    postalCode: placeData?.place.postalCode || '',
                    streetNumber: placeData?.place.streetNumber || '',
                    administrativeAreaLevel1: placeData?.place.administrativeAreaLevel1 || '',
                    administrativeAreaLevel2: placeData?.place.administrativeAreaLevel2 || '',
                    country: placeData?.place.country || '',
                    name: placeData?.place.name || '',
                    provider: placeData?.place.provider || '',
                    providerId: placeData?.place.providerId || '',
                    lat: placeData?.place.lat ?? 0,
                    lng: placeData?.place.lng ?? 0,
                    additionalInfo: additionalAddressInfo || '',
                },
                token: token || '',
            });
            return;
        }
    };

    useEffect(() => {
        if (!isLoading && isSuccess) {
            handleHide();
            window.location.reload();
        }
    }, [isLoading, isSuccess, handleHide]);

    return {
        form,
        currentStep,
        steps,
        errorMessage,
        handleHide,
        isLoading,
        onFormValidate,
    };
};
